import * as React from 'react'

import { Dropdown } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { useEmbroideryForm } from '../../../../containers/EmbroideryService/useEmbroideryForm'
import { trackEvent } from '../../../../lib/analytics'
import { EmbroideryVariant } from '../../../../types/graphql'
import { StyledHr } from '../DTCLogoEmbroideryForm/EmbroideryLogoSetup/styles'
import { IFormProps } from '../IFormProps'
import { TextPreviewItem } from '../PreviewItems/TextPreviewItem'
import { EmbroideryOptionWrapper, FormBody } from '../shared-form-styles'
import { SubmissionButtons } from '../SubmissionButtons'

import { Font } from './FormBody/Font'
import * as Name from './FormBody/Name'
import { NameColor } from './FormBody/NameColor'
import { TextPlacement } from './FormBody/TextPlacement'
import { useValidationSchema } from './useValidationSchema'

export const TextEmbroideryForm: React.FC<IFormProps<EmbroideryVariant>> = ({
  embroideryOption,
  hideFields = [],
  heapCtaAction = 'form submit',
}) => {
  const { currentlySelectedFormOption, textEmbroideryOptions, selectedFormKind } =
    EmbroideryService.useContainer()

  const maxCharacterCount = embroideryOption.textMaxChars ?? 23
  const validationSchema = useValidationSchema({
    textMaxChars: maxCharacterCount,
    textEmbroideryOptions,
  })
  const { form, resetForm } = useEmbroideryForm('TEXT_ONLY', {
    validationSchema,
    createEmbroideryOptions: values => ({
      textColor: values.textColor,
      textFirstLine: values.firstLine,
      textFont: values.font?.name,
      textPlacement: values.textPlacement?.name,
      textSecondLine: values.secondLine,
      embroideryItemVariant: embroideryOption.variants[0],
    }),
    previewItem: form => <TextPreviewItem values={form.values} />,
  })

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  return (
    <FormBody
      onSubmit={form.handleSubmit}
      {...trackEvent({
        category: 'embroidery',
        action: heapCtaAction,
        label: 'text embroidery form',
      })}
    >
      {!hideFields.includes('firstLine') && (
        <Name.Wrapper label='first-line-text'>
          <Name.Input
            ariaLabelledby='first-line-text'
            name='firstLine'
            form={form}
            maxCharacterCount={maxCharacterCount}
            placeholder={fixtures.textOption.firstLinePlaceholder}
          />
        </Name.Wrapper>
      )}
      {!hideFields.includes('secondLine') && embroideryOption.textLines === 2 && (
        <Name.Wrapper label='second-line-text' title={fixtures.textOption.secondLinePlaceholder}>
          {(textEmbroideryOptions?.secondLine ?? []).length > 0 ? (
            <Dropdown
              data-testid='second-line-dropdown'
              isInvalid={Boolean(form.errors.secondLine)}
              messageWhenInvalid={form.errors.secondLine}
              onChange={sel => form.setFieldValue('secondLine', sel)}
              options={textEmbroideryOptions?.secondLine ?? []}
              placeholderText={'Select Title'}
              selection={form.values.secondLine}
              variant='box'
            />
          ) : (
            <Name.Input
              ariaLabelledby='second-line-text'
              name='secondLine'
              form={form}
              maxCharacterCount={maxCharacterCount}
              placeholder={fixtures.textOption.secondLinePlaceholder}
            />
          )}
        </Name.Wrapper>
      )}

      {!hideFields.includes('textPlacement') && (
        <TextPlacement
          labelId='text-placement-for-text-only'
          placementOptions={embroideryOption.textPlacements}
          form={form}
          kind={selectedFormKind}
        />
      )}

      {!hideFields.includes('textColor') && (
        <NameColor form={form} textColors={embroideryOption.textColors} />
      )}

      {!hideFields.includes('font') && (
        <Font
          labelId='font-selector-for-text-only'
          form={form}
          textFonts={embroideryOption.textFonts}
        />
      )}

      <EmbroideryOptionWrapper>
        <StyledHr />
        <SubmissionButtons
          displayPrice={currentlySelectedFormOption?.variants[0]?.price}
          onReset={() => {
            resetForm()
          }}
          heapCtaAction={heapCtaAction}
        />
      </EmbroideryOptionWrapper>
    </FormBody>
  )
}
